import { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import { toast } from "sonner";

function confidenceBadge(c) {
  if (c === "high") return "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
  if (c === "medium") return "bg-amber-500/10 text-amber-400 border-amber-500/20";
  return "bg-zinc-700/50 text-zinc-400 border-zinc-600";
}

export default function AiEstimateSection({ releaseId }) {
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleEstimate = async () => {
    setLoading(true);
    setEstimate(null);
    try {
      const { data } = await api.post("/ai/estimate-value", { release_id: releaseId });
      setEstimate(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI estimation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-400" />
          <h3 className="font-heading text-lg font-semibold text-zinc-100">AI Value Estimate</h3>
          <span className="text-[10px] bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2 py-0.5 rounded-full font-medium">GPT-5.2</span>
        </div>
        <Button
          data-testid="ai-estimate-btn"
          onClick={handleEstimate}
          disabled={loading}
          size="sm"
          className="bg-gradient-to-r from-amber-600 to-rose-600 hover:from-amber-500 hover:to-rose-500 text-white border-0 shadow-[0_0_12px_rgba(245,158,11,0.2)]"
        >
          {loading ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Analyzing...</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Estimate Value</>}
        </Button>
      </div>
      {estimate && (
        <div className="mt-4 space-y-3 animate-fadeIn">
          <div className="flex items-center gap-4 flex-wrap">
            <div>
              <p className="text-[11px] text-zinc-500 uppercase tracking-wider">AI Estimated Value</p>
              <p className="font-mono text-2xl font-bold text-amber-400" data-testid="ai-estimate-value">
                {estimate.ai_estimated_value != null ? `$${estimate.ai_estimated_value.toFixed(2)}` : "Unable to estimate"}
              </p>
              {estimate.price_range && estimate.price_range.low != null && estimate.price_range.high != null && (
                <p className="text-[11px] text-zinc-500 mt-0.5 font-mono" data-testid="ai-estimate-range">
                  range ${Number(estimate.price_range.low).toFixed(2)}–${Number(estimate.price_range.high).toFixed(2)}
                </p>
              )}
            </div>
            {estimate.current_value != null && estimate.ai_estimated_value != null && (
              <div>
                <p className="text-[11px] text-zinc-500 uppercase tracking-wider">Current Listed</p>
                <p className="font-mono text-lg text-zinc-400">${estimate.current_value.toFixed(2)}</p>
              </div>
            )}
            <div className="ml-auto">
              <span className={`text-xs font-medium px-2 py-1 rounded-full border ${confidenceBadge(estimate.confidence)}`}>
                {estimate.confidence} confidence
              </span>
            </div>
          </div>
          <p className="text-sm text-zinc-400 leading-relaxed border-t border-zinc-800/50 pt-3">{estimate.reasoning}</p>
        </div>
      )}
    </div>
  );
}
